import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { persist, createJSONStorage } from 'zustand/middleware'
import type { } from '@redux-devtools/extension'; // required for devtools typing

import type { ICabinet } from 'models/cabinet';
import type { IQuote } from 'models/quote';

import defaultCabinets from './defaultCabinets.json';

export const defaultVersion = '1.0.0';

/**
 * Checks if the version stored in local storage is the same as the default version.
 *
 * @returns {boolean} Whether the version is the same as the default version.
 */
export function isSameVersion(): boolean {
    const version = localStorage.getItem('version');
    if (!version) {
        return false;
    }

    return version === defaultVersion;
};

interface CabinetState {
    cabinets: ICabinet[];
    selectedCabinet?: ICabinet;

    setCabinets: (cabinets: ICabinet[]) => void;
    addCabinet: (cabinet: ICabinet) => void;
    removeCabinet: (cabinet: ICabinet) => void;
    setSelectedCabinet: (cabinet: ICabinet) => void;
    clearSelectedCabinet: () => void;
    resetCabinets: () => void;
}

export const useCabinetStore = create<CabinetState>()(
    devtools(
        persist(
            (set) => ({
                cabinets: defaultCabinets as ICabinet[],
                selectedCabinet: undefined,
                version: defaultVersion,

                setCabinets: (cabinets) => set({ cabinets }),
                addCabinet: (cabinet) => set((state) => ({ cabinets: [...state.cabinets, cabinet] })),
                removeCabinet: (cabinet) => set((state) => ({ cabinets: state.cabinets.filter((c) => c !== cabinet) })),
                setSelectedCabinet: (selectedCabinet) => set({ selectedCabinet }),
                clearSelectedCabinet: () => set({ selectedCabinet: undefined }),
                resetCabinets: () => set({ cabinets: defaultCabinets as ICabinet[], selectedCabinet: undefined }),
            }),
            {
                name: 'cabinet-store',
                storage: createJSONStorage(() => localStorage),
            },
        ),
        { name: 'Cabinet Store', trace: true},
    ),
);

interface QuoteState {
    quote?: IQuote;
    requestedWidth?: number;
    requestedHeight?: number;

    setQuote: (quote: IQuote) => void;
    setRequestedWidth: (requestedWidth: number) => void;
    setRequestedHeight: (requestedHeight: number) => void;
    clearQuote: () => void;
}

export const useQuoteStore = create<QuoteState>()(
    devtools(
        persist(
            (set) => ({
                quote: undefined,

                setQuote: (quote) => set({ quote }),
                setRequestedWidth: (requestedWidth) => set({ requestedWidth }),
                setRequestedHeight: (requestedHeight) => set({ requestedHeight }),
                clearQuote: () => set({ quote: undefined }),
            }),
            {
                name: 'quote-store',
                storage: createJSONStorage(() => localStorage),
            },
        ),
        { name: 'Quote Store', trace: true },
    ),
);
